import { motion, useMotionValue, useTransform, useSpring } from 'framer-motion';
import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { fetchReelsVideos } from '../services/youtube';
import type { YouTubeVideo } from '../services/youtube';

// 3D Tilt + autoplay-on-scroll card
function ReelCard({
  reel,
  index,
  isPlaying,
  onPlay,
  onClose,
}: {
  reel: YouTubeVideo;
  index: number;
  isPlaying: boolean;
  onPlay: () => void;
  onClose: () => void;
}) {
  const cardRef = useRef<HTMLDivElement>(null);

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const rotateX = useSpring(useTransform(y, [-0.5, 0.5], [8, -8]), { stiffness: 300, damping: 30 });
  const rotateY = useSpring(useTransform(x, [-0.5, 0.5], [-8, 8]), { stiffness: 300, damping: 30 });

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    x.set((e.clientX - rect.left) / rect.width - 0.5);
    y.set((e.clientY - rect.top) / rect.height - 0.5);
  }, [x, y]);

  const handleMouseLeave = useCallback(() => {
    x.set(0);
    y.set(0);
  }, [x, y]);

  // Parse title
  const parts = reel.title.split(/[-–—]/).map(s => s.trim());
  const sub = parts.length >= 2 ? parts.slice(0, -1).join(' · ') : '';
  const main = parts.length >= 2 ? parts[parts.length - 1] : reel.title;

  return (
    <motion.div
      ref={cardRef}
      className="relative cursor-pointer"
      style={{ aspectRatio: '9/16', perspective: 800, rotateX, rotateY, transformStyle: 'preserve-3d' }}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: Math.min(index * 0.05, 0.4) }}
      whileHover={{ scale: 1.08, zIndex: 10 }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={isPlaying ? undefined : onPlay}
    >
      {/* Cyan glow on hover */}
      <motion.div
        className="absolute inset-0 rounded-xl pointer-events-none z-20"
        initial={{ opacity: 0 }}
        whileHover={{ opacity: 1 }}
        style={{ boxShadow: '0 0 40px rgba(11,227,197,0.5), inset 0 0 0 1.5px rgba(11,227,197,0.4)' }}
      />

      <div className="absolute inset-0 overflow-hidden rounded-xl bg-black">
        {isPlaying ? (
          <>
            <iframe
              src={`${reel.embedUrl}&autoplay=1`}
              title={reel.title}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              referrerPolicy="strict-origin-when-cross-origin"
            />
            <button
              onClick={e => { e.stopPropagation(); onClose(); }}
              className="absolute top-2 right-2 p-2 bg-black/70 hover:bg-black/90 rounded-lg backdrop-blur-sm transition-colors z-30"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className="text-white">
                <path d="M12 4L4 12M4 4L12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </>
        ) : (
          <>
            <img
              src={reel.thumbnail}
              alt={reel.title}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700"
              loading="lazy"
            />

            {/* Bottom gradient for text */}
            <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />

            {/* Play button */}
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center"
                style={{ background: 'rgba(11,227,197,0.2)', border: '2px solid #0be3c5', boxShadow: '0 0 30px rgba(11,227,197,0.5)' }}
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="#0be3c5" className="ml-1">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            </div>

            {/* Title overlay */}
            <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4 z-10">
              {sub && (
                <p className="text-white/60 text-[10px] md:text-xs tracking-widest uppercase mb-1 font-medium">
                  {sub}
                </p>
              )}
              <h3
                className="text-white font-black uppercase leading-none"
                style={{ fontSize: 'clamp(13px, 3vw, 20px)', textShadow: '0 2px 12px rgba(0,0,0,0.9)' }}
              >
                {main}
              </h3>
            </div>
          </>
        )}
      </div>
    </motion.div>
  );
}

export default function Reels() {
  const [reels, setReels] = useState<YouTubeVideo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [playingVideo, setPlayingVideo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadReels = async () => {
      try {
        const reelsVideos = await fetchReelsVideos();
        setReels(reelsVideos);
        if (reelsVideos.length === 0) setError('No reels found in the playlist.');
      } catch {
        setError('Failed to load reels. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    loadReels();
  }, []);

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="section-padding pb-12">
        <div className="px-6 md:px-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-white/40 text-sm tracking-[0.3em] uppercase">Social Media</span>
            <h1 className="text-5xl md:text-6xl font-bold mt-2 mb-6">Reels</h1>
            <p className="text-white/60 text-lg max-w-2xl">
              Short-form content optimized for Instagram, TikTok, and YouTube Shorts.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Reels Grid */}
      <section className="pb-16 px-6 md:px-10">
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={32} className="text-white/40 animate-spin" />
          </div>
        ) : error ? (
          <div className="text-center py-20">
            <p className="text-red-400 mb-4">{error}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" style={{ perspective: '1000px' }}>
            {reels.map((reel, index) => (
              <ReelCard
                key={reel.id}
                reel={reel}
                index={index}
                isPlaying={playingVideo === reel.videoId}
                onPlay={() => setPlayingVideo(reel.videoId)}
                onClose={() => setPlayingVideo(null)}
              />
            ))}
          </div>
        )}
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#0a0a0a]">
        <div className="container-custom">
          <motion.div
            className="text-center max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Need Social Media Content?</h2>
            <p className="text-white/60 mb-8">
              Let's create scroll-stopping reels that boost your engagement and grow your audience.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/contact" className="btn-primary inline-flex items-center justify-center">Get Started</a>
              <a href="/work" className="btn-outline inline-flex items-center justify-center">View Full Portfolio</a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
